import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  getLastNDaysDateRange,
  getPreviousPeriodDateRange,
  getPreviousYearDateRange,
} from 'src/utils/dateUtils';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Collapse,
  IconButton,
  Autocomplete,
  Checkbox,
  Tooltip,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import { useQuery } from '@apollo/client';
import { useTheme } from '@mui/material/styles';
import palette from 'src/theme/palette';
import { KIPIFY_FILTER_APPLY_BUTTON_CLASS } from 'src/theme/kipifyUi';
import { useTrialStatus } from 'src/hooks/useTrialStatus';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { useSidebar } from 'src/providers/sidebar/useSidebar';
import {
  getLookbackCompareAutoDisabledMessage,
  getLookbackCompareBlockedMessage,
  getLookbackCustomRangeBlockedMessage,
  getLookbackPlanLimitCaption,
  getLookbackPresetBlockedMessage,
} from 'src/utils/lookbackMessages';
import { GET_INVENTORY_DATA } from 'src/utils/gql/inventoryQueries';
import { FILTER_COUNTRY_OPTIONS } from 'src/constants/countryOptions';

const barPulseKeyframes = keyframes({
  '0%, 100%': { opacity: 0.4, transform: 'scaleY(0.7)' },
  '50%': { opacity: 1, transform: 'scaleY(1)' },
});

const insightGlowKeyframes = keyframes({
  '0%': { boxShadow: '0 0 0 0 rgba(0,0,0,0)' },
  '50%': {
    boxShadow: `0 4px 20px -4px ${palette.primary.main}40, 0 0 0 1px ${palette.primary.main}20`,
  },
  '100%': { boxShadow: '0 2px 12px -2px rgba(0,0,0,0.06)' },
});

const CUMULATIVE_THRESHOLD_PCT = 80;

interface ParetoProduct {
  productId: string;
  productTitle: string;
  imageUrl?: string;
  totalSold: number;
  totalRevenue: number;
  revenueShare: number;
  cumulativeShare: number;
  /** % di prodotti (rank): i primi N prodotti sono il productSharePct% del catalogo */
  productSharePct: number;
  paretoIndex: number;
}

const PageContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  minHeight: '100vh',
  width: '100%',
  boxSizing: 'border-box',
  overflow: 'visible',
  [theme.breakpoints.up('sm')]: {
    marginLeft: '240px',
    width: 'calc(100% - 240px)',
    maxWidth: 'calc(100% - 240px)',
  },
  [theme.breakpoints.down('md')]: {
    marginLeft: '220px',
    width: 'calc(100% - 220px)',
    maxWidth: 'calc(100% - 220px)',
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
    width: '100%',
    marginTop: '60px',
    maxWidth: '100%',
  },
}));

const ContentContainer = styled(Box)(() => ({
  width: '100%',
  maxWidth: '100%',
  margin: 0,
  padding: '16px',
  boxSizing: 'border-box',
  overflow: 'visible',
  minHeight: '100%',
}));

const filterInputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: palette.common.white,
    '& fieldset': { borderColor: palette.primary.main },
  },
};

const Pareto: React.FC = () => {
  const snackbar = useSnackbar();
  const theme = useTheme();
  const { collapsed: sidebarCollapsed } = useSidebar();
  const { lookbackDays } = useTrialStatus();
  const containerSx = sidebarCollapsed
    ? {
        [theme.breakpoints.up('sm')]: {
          marginLeft: '72px',
          width: 'calc(100% - 72px)',
          maxWidth: 'calc(100% - 72px)',
        },
      }
    : undefined;

  const [dateRange, setDateRange] = useState(() => getLastNDaysDateRange(30));
  const [dateRangePreset, setDateRangePreset] = useState<string>('30');
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [tempCustomDates, setTempCustomDates] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [compareMode, setCompareMode] = useState<string>('none');
  const [compareDateRange, setCompareDateRange] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [showCustomCompareDates, setShowCustomCompareDates] = useState(false);
  const [tempCustomCompareDates, setTempCustomCompareDates] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [countryFilter, setCountryFilter] = useState<string[]>([]);
  const [customerTypeFilter, setCustomerTypeFilter] = useState<string>('');
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [collectionFilter, setCollectionFilter] = useState<string[]>([]);
  const [vendorFilter, setVendorFilter] = useState<string[]>([]);
  const [productTypeFilter, setProductTypeFilter] = useState<string[]>([]);

  const activeFilterCount =
    countryFilter.length +
    (customerTypeFilter ? 1 : 0) +
    tagFilter.length +
    collectionFilter.length +
    vendorFilter.length +
    productTypeFilter.length;

  useEffect(() => {
    if (compareMode === 'previous_period') {
      setCompareDateRange(getPreviousPeriodDateRange(dateRange));
    } else if (compareMode === 'previous_year') {
      setCompareDateRange(getPreviousYearDateRange(dateRange));
    }
  }, [dateRange, compareMode]);

  const lookbackMinDate = useMemo(() => {
    if (lookbackDays === null || lookbackDays === undefined) return null;
    const min = new Date();
    min.setDate(min.getDate() - lookbackDays);
    return min.toISOString().split('T')[0];
  }, [lookbackDays]);

  const isRangeWithinLookback = useCallback(
    (range: { from: string; to: string } | null | undefined) => {
      if (!range || !lookbackMinDate) return true;
      return range.from >= lookbackMinDate;
    },
    [lookbackMinDate],
  );

  const canUsePresetDays = useCallback(
    (days: number) => {
      if (lookbackDays === null || lookbackDays === undefined) return true;
      return days <= lookbackDays;
    },
    [lookbackDays],
  );

  const previousPeriodRangeCandidate = useMemo(
    () => getPreviousPeriodDateRange(dateRange),
    [dateRange],
  );
  const previousYearRangeCandidate = useMemo(
    () => getPreviousYearDateRange(dateRange),
    [dateRange],
  );
  const canUsePreviousPeriodCompare = useMemo(
    () => isRangeWithinLookback(previousPeriodRangeCandidate),
    [isRangeWithinLookback, previousPeriodRangeCandidate],
  );
  const canUsePreviousYearCompare = useMemo(
    () => isRangeWithinLookback(previousYearRangeCandidate),
    [isRangeWithinLookback, previousYearRangeCandidate],
  );

  useEffect(() => {
    if (compareMode === 'previous_period' && !canUsePreviousPeriodCompare) {
      setCompareMode('none');
      setCompareDateRange(null);
      snackbar(
        getLookbackCompareAutoDisabledMessage('previous_period', lookbackDays),
        'warning',
      );
    } else if (compareMode === 'previous_year' && !canUsePreviousYearCompare) {
      setCompareMode('none');
      setCompareDateRange(null);
      snackbar(
        getLookbackCompareAutoDisabledMessage('previous_year', lookbackDays),
        'warning',
      );
    }
  }, [
    compareMode,
    canUsePreviousPeriodCompare,
    canUsePreviousYearCompare,
    lookbackDays,
    snackbar,
  ]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const { data, loading, error } = useQuery(GET_INVENTORY_DATA, {
    variables: {
      dateRange: { from: dateRange.from, to: dateRange.to },
      ...(countryFilter?.length ? { countries: countryFilter } : {}),
      ...(customerTypeFilter ? { customerType: customerTypeFilter } : {}),
    },
    fetchPolicy: 'network-only',
  });

  const inventoryData = data?.getInventoryData ?? [];

  const availableTags = useMemo(() => {
    const set = new Set<string>();
    inventoryData.forEach((p: { tags?: string[] }) =>
      p.tags?.forEach((t) => set.add(t)),
    );
    return Array.from(set).sort();
  }, [inventoryData]);

  const availableCollections = useMemo(() => {
    const set = new Set<string>();
    inventoryData.forEach((p: { collections?: string[] }) =>
      p.collections?.forEach((c) => set.add(c)),
    );
    return Array.from(set).sort();
  }, [inventoryData]);

  const availableVendors = useMemo(() => {
    const set = new Set<string>();
    inventoryData.forEach((p: { vendor?: string }) => {
      if (p.vendor) set.add(p.vendor);
    });
    return Array.from(set).sort();
  }, [inventoryData]);

  const availableProductTypes = useMemo(() => {
    const set = new Set<string>();
    inventoryData.forEach((p: { productType?: string }) => {
      if (p.productType) set.add(p.productType);
    });
    return Array.from(set).sort();
  }, [inventoryData]);

  /** Filtri “strutturali” (tag, collection, …): definiscono l’universo Pareto e i totali % */
  const structuralFilteredData = useMemo(() => {
    let filtered = inventoryData;
    if (tagFilter.length > 0) {
      filtered = filtered.filter((p: { tags?: string[] }) =>
        p.tags?.some((t) => tagFilter.includes(t)),
      );
    }
    if (collectionFilter.length > 0) {
      filtered = filtered.filter((p: { collections?: string[] }) =>
        p.collections?.some((c) => collectionFilter.includes(c)),
      );
    }
    if (vendorFilter.length > 0) {
      filtered = filtered.filter(
        (p: { vendor?: string }) => p.vendor && vendorFilter.includes(p.vendor),
      );
    }
    if (productTypeFilter.length > 0) {
      filtered = filtered.filter(
        (p: { productType?: string }) =>
          p.productType && productTypeFilter.includes(p.productType),
      );
    }
    return filtered;
  }, [
    inventoryData,
    tagFilter,
    collectionFilter,
    vendorFilter,
    productTypeFilter,
  ]);

  /** Ricerca testuale: limita solo le righe in tabella, non i denominatori % revenue / % prodotti */
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return structuralFilteredData;
    const q = searchTerm.toLowerCase();
    return structuralFilteredData.filter(
      (p: {
        productTitle: string;
        variants?: { variantTitle?: string; sku?: string }[];
      }) =>
        p.productTitle.toLowerCase().includes(q) ||
        p.variants?.some(
          (v) =>
            v.variantTitle?.toLowerCase().includes(q) ||
            v.sku?.toLowerCase().includes(q),
        ),
    );
  }, [structuralFilteredData, searchTerm]);

  const fullParetoData = useMemo<ParetoProduct[]>(() => {
    if (!structuralFilteredData.length) return [];
    const sorted = [...structuralFilteredData].sort(
      (a: { totalRevenue?: number }, b: { totalRevenue?: number }) =>
        (b.totalRevenue || 0) - (a.totalRevenue || 0),
    );
    const total = sorted.reduce(
      (s, p: { totalRevenue?: number }) => s + (p.totalRevenue || 0),
      0,
    );
    if (!total) return [];
    const totalProducts = sorted.length;
    let cumulative = 0;
    return sorted.map(
      (
        p: {
          productId: string;
          productTitle: string;
          imageUrl?: string;
          totalSold: number;
          totalRevenue?: number;
        },
        i: number,
      ) => {
        const rev = p.totalRevenue || 0;
        cumulative += rev;
        return {
          productId: p.productId,
          productTitle: p.productTitle,
          imageUrl: p.imageUrl,
          totalSold: p.totalSold,
          totalRevenue: rev,
          revenueShare: (rev / total) * 100,
          cumulativeShare: (cumulative / total) * 100,
          productSharePct: ((i + 1) / totalProducts) * 100,
          paretoIndex: i + 1,
        };
      },
    );
  }, [structuralFilteredData]);

  /** Righe tabella: tutte le posizioni Pareto o solo quelle che matchano la ricerca (ordine globale invariato) */
  const displayParetoData = useMemo(() => {
    if (!fullParetoData.length) return [];
    if (!searchTerm.trim()) return fullParetoData;
    const ids = new Set(
      filteredData.map((p: { productId: string }) => p.productId),
    );
    return fullParetoData.filter((row) => ids.has(row.productId));
  }, [fullParetoData, filteredData, searchTerm]);

  /** Quanti prodotti (N) generano l'80% del fatturato → "80% revenue from top X% of products (N items)" */
  const top80Stats = useMemo(() => {
    if (!fullParetoData.length) return null;
    const lastInTop80 = fullParetoData.findIndex(
      (r) => r.cumulativeShare > CUMULATIVE_THRESHOLD_PCT,
    );
    const count = lastInTop80 === -1 ? fullParetoData.length : lastInTop80;
    const productPct = (count / fullParetoData.length) * 100;
    return { count, productPct, totalProducts: fullParetoData.length };
  }, [fullParetoData]);

  // Chart: ordine decrescente (alto impatto sinistra → basso destra); altezza barra = % revenue (sempre sull’universo filtrato, senza ricerca)
  const chartBars = useMemo(() => {
    const slice = fullParetoData.slice(0, 300);
    const maxShare = Math.max(...slice.map((r) => r.revenueShare), 0.01);
    return slice.map((row) => ({
      ...row,
      heightPct: (row.revenueShare / maxShare) * 100,
    }));
  }, [fullParetoData]);

  const fmtEur = (v: number | undefined | null) => {
    if (typeof v !== 'number' || !Number.isFinite(v)) return '—';
    const n = Math.abs(v);
    const [int, dec] = n.toFixed(2).split('.');
    const withDots = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return (v < 0 ? '−' : '') + withDots + ',' + dec + ' €';
  };

  if (error) {
    return (
      <PageContainer sx={containerSx}>
        <ContentContainer>
          <Typography color="error">{error.message}</Typography>
        </ContentContainer>
      </PageContainer>
    );
  }

  const periodLabel =
    dateRangePreset === 'custom'
      ? `${dateRange.from} → ${dateRange.to}`
      : `Last ${dateRangePreset} days`;

  const controlHeight = 40;
  const controlRadius = 2;
  const controlSx = {
    height: controlHeight,
    minHeight: controlHeight,
    borderRadius: controlRadius,
    backgroundColor: 'white',
    '& .MuiOutlinedInput-root': {
      height: controlHeight,
      minHeight: controlHeight,
      borderRadius: controlRadius,
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: palette.primary.main },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: palette.primary.main,
    },
  };
  const labelSx = {
    display: 'block',
    mb: 0.5,
    fontSize: '0.7rem',
    fontWeight: 600,
    color: palette.grey[500],
  };

  return (
    <PageContainer sx={containerSx}>
      <ContentContainer>
        {/* Banda accent top */}
        <Box
          sx={{
            height: 4,
            borderRadius: '0 0 4px 4px',
            background: `linear-gradient(90deg, ${palette.primary.main}, ${palette.primary.light})`,
            mb: 2,
            mx: -0.5,
          }}
        />

        {/* Header: sezione + titolo + insight */}
        <Box
          sx={{
            mb: 2,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: palette.grey[200],
            background: `linear-gradient(180deg, ${palette.primary.main}05 0%, ${palette.primary.main}02 25%, transparent 70%)`,
            mt: -0.5,
            pt: 2,
            px: 2,
            mx: -2,
            borderRadius: 2,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 1,
              background: `linear-gradient(90deg, transparent, ${palette.primary.main}18, transparent)`,
              opacity: 0.6,
            },
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.25,
              py: 0.5,
              borderRadius: '999px',
              bgcolor: `${palette.primary.main}18`,
              border: '1px solid',
              borderColor: `${palette.primary.main}35`,
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: palette.primary.main,
                flexShrink: 0,
                boxShadow: `0 0 0 2px ${palette.primary.main}40`,
              }}
            />
            <Typography
              sx={{
                color: palette.primary.main,
                fontWeight: 700,
                letterSpacing: 1.1,
                fontSize: '0.7rem',
              }}
            >
              PRODUCTS ANALYSIS
            </Typography>
          </Box>
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <Typography
              variant="h4"
              sx={{
                color: palette.grey[800],
                fontWeight: 800,
                letterSpacing: '-0.025em',
                lineHeight: 1.2,
              }}
            >
              Where your revenue really comes from
            </Typography>
            <Box
              sx={{
                width: '60%',
                height: 3,
                borderRadius: '0 0 2px 2px',
                background: `linear-gradient(90deg, ${palette.primary.main}, transparent)`,
                opacity: 0.5,
                mt: 0.5,
              }}
            />
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, mb: 1.5, maxWidth: 480 }}
          >
            See which products drive most of your sales—at a glance.
          </Typography>
          {top80Stats && top80Stats.count > 0 && (
            <Box
              sx={{
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 0.5,
                px: 2.5,
                py: 1.5,
                borderRadius: 2,
                background: `linear-gradient(135deg, rgba(255,255,255,0.95) 0%, ${palette.primary.main}08 100%)`,
                border: '1px solid',
                borderColor: `${palette.primary.main}25`,
                borderLeft: '5px solid',
                borderLeftColor: palette.primary.main,
                boxShadow: `0 4px 20px -4px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.8)`,
                animation: `${insightGlowKeyframes} 1.2s ease-out 1`,
                position: 'relative',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: palette.grey[600],
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                }}
              >
                Key insight
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontSize: '1.75rem',
                    fontWeight: 800,
                    color: palette.primary.main,
                    textShadow: `0 0 24px ${palette.primary.main}35`,
                    letterSpacing: '-0.03em',
                    lineHeight: 1,
                  }}
                >
                  {CUMULATIVE_THRESHOLD_PCT}%
                </Typography>
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ color: palette.grey[600] }}
                >
                  of revenue from the top{' '}
                  <strong>{top80Stats.productPct.toFixed(0)}%</strong> of
                  products sold
                  {' · '}
                  <Box
                    component="span"
                    sx={{
                      fontSize: '0.75rem',
                      color: palette.grey[800],
                      fontWeight: 600,
                    }}
                  >
                    {top80Stats.count} of {top80Stats.totalProducts} products
                  </Box>
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Toolbar: strip unificata */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 1.5,
            mb: 1.5,
            flexWrap: 'wrap',
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'rgba(0,0,0,0.02)',
            border: '1px solid',
            borderColor: palette.grey[200],
          }}
        >
          <Box>
            <Typography component="label" sx={labelSx}>
              Period
            </Typography>
            <FormControl size="small" sx={{ width: 145, ...controlSx }}>
              <Select
                value={dateRangePreset}
                onChange={(e) => {
                  const preset = e.target.value;
                  if (preset !== 'custom') {
                    const days = parseInt(preset, 10);
                    if (!isNaN(days) && !canUsePresetDays(days)) {
                      snackbar(
                        getLookbackPresetBlockedMessage(lookbackDays),
                        'warning',
                      );
                      return;
                    }
                  }
                  setDateRangePreset(preset);
                  setShowCustomDateRange(preset === 'custom');
                  setTempCustomDates(null);
                  if (preset !== 'custom') {
                    const days = parseInt(preset, 10);
                    const newRange = getLastNDaysDateRange(days);
                    setDateRange(newRange);
                    if (compareMode === 'previous_period')
                      setCompareDateRange(getPreviousPeriodDateRange(newRange));
                    else if (compareMode === 'previous_year')
                      setCompareDateRange(getPreviousYearDateRange(newRange));
                  } else {
                    setTempCustomDates({
                      from: dateRange.from,
                      to: dateRange.to,
                    });
                  }
                }}
                sx={{
                  '& .MuiSelect-select': {
                    py: 0,
                    height: controlHeight - 2,
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              >
                <MenuItem value="7" disabled={!canUsePresetDays(7)}>
                  Last 7 days
                </MenuItem>
                <MenuItem value="30" disabled={!canUsePresetDays(30)}>
                  Last 30 days
                </MenuItem>
                <MenuItem value="90" disabled={!canUsePresetDays(90)}>
                  Last 90 days
                </MenuItem>
                <MenuItem value="custom">Custom range</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {showCustomDateRange && (
            <>
              <TextField
                type="date"
                label="From"
                size="small"
                value={tempCustomDates?.from ?? dateRange.from}
                onChange={(e) => {
                  const v = e.target.value;
                  const t = tempCustomDates?.to ?? dateRange.to;
                  setTempCustomDates(
                    t && v > t
                      ? { from: v, to: v }
                      : { from: v, to: tempCustomDates?.to ?? dateRange.to },
                  );
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: lookbackMinDate || undefined,
                  max: (tempCustomDates?.to ?? dateRange.to) || undefined,
                }}
                sx={{ width: 140, ...controlSx }}
              />
              <TextField
                type="date"
                label="To"
                size="small"
                value={tempCustomDates?.to ?? dateRange.to}
                onChange={(e) => {
                  const v = e.target.value;
                  const f = tempCustomDates?.from ?? dateRange.from;
                  setTempCustomDates(
                    f && v < f
                      ? { from: v, to: v }
                      : {
                          from: tempCustomDates?.from ?? dateRange.from,
                          to: v,
                        },
                  );
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: (tempCustomDates?.from ?? dateRange.from) || undefined,
                  max: new Date().toISOString().split('T')[0],
                }}
                sx={{ width: 140, ...controlSx }}
              />
              <Button
                variant="contained"
                size="small"
                className={KIPIFY_FILTER_APPLY_BUTTON_CLASS}
                onClick={() => {
                  if (
                    tempCustomDates?.from &&
                    tempCustomDates?.to &&
                    tempCustomDates.from <= tempCustomDates.to
                  ) {
                    if (!isRangeWithinLookback(tempCustomDates)) {
                      snackbar(
                        getLookbackCustomRangeBlockedMessage(
                          lookbackDays,
                          'date',
                        ),
                        'warning',
                      );
                      return;
                    }
                    setDateRange(tempCustomDates);
                    setTempCustomDates(null);
                  }
                }}
                disabled={
                  !tempCustomDates?.from ||
                  !tempCustomDates?.to ||
                  tempCustomDates.from > tempCustomDates.to
                }
                sx={{
                  minHeight: controlHeight,
                  borderRadius: controlRadius,
                  bgcolor: palette.primary.main,
                  color: palette.primary.contrastText,
                  minWidth: 60,
                }}
              >
                Apply
              </Button>
            </>
          )}

          <Box>
            <Typography component="label" sx={labelSx}>
              Compare
            </Typography>
            <FormControl size="small" sx={{ width: 155, ...controlSx }}>
              <Select
                value={compareMode}
                onChange={(e) => {
                  const mode = e.target.value;
                  if (
                    mode === 'previous_period' &&
                    !canUsePreviousPeriodCompare
                  ) {
                    snackbar(
                      getLookbackCompareBlockedMessage(
                        'previous_period',
                        lookbackDays,
                      ),
                      'warning',
                    );
                    return;
                  }
                  if (mode === 'previous_year' && !canUsePreviousYearCompare) {
                    snackbar(
                      getLookbackCompareBlockedMessage(
                        'previous_year',
                        lookbackDays,
                      ),
                      'warning',
                    );
                    return;
                  }
                  setCompareMode(mode);
                  if (mode === 'none') {
                    setCompareDateRange(null);
                    setShowCustomCompareDates(false);
                    setTempCustomCompareDates(null);
                  } else if (mode === 'previous_period') {
                    setCompareDateRange(getPreviousPeriodDateRange(dateRange));
                    setShowCustomCompareDates(false);
                  } else if (mode === 'previous_year') {
                    setCompareDateRange(getPreviousYearDateRange(dateRange));
                    setShowCustomCompareDates(false);
                  } else if (mode === 'custom') {
                    setShowCustomCompareDates(true);
                    setTempCustomCompareDates(
                      compareDateRange || {
                        from: dateRange.from,
                        to: dateRange.to,
                      },
                    );
                  }
                }}
                sx={{
                  '& .MuiSelect-select': {
                    py: 0,
                    height: controlHeight - 2,
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem
                  value="previous_period"
                  disabled={!canUsePreviousPeriodCompare}
                >
                  Previous period
                </MenuItem>
                <MenuItem
                  value="previous_year"
                  disabled={!canUsePreviousYearCompare}
                >
                  Previous year
                </MenuItem>
                <MenuItem value="custom">Custom range</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {showCustomCompareDates && (
            <>
              <TextField
                type="date"
                label="From"
                size="small"
                value={
                  tempCustomCompareDates?.from ?? compareDateRange?.from ?? ''
                }
                onChange={(e) => {
                  const v = e.target.value;
                  const t = tempCustomCompareDates?.to ?? compareDateRange?.to;
                  setTempCustomCompareDates(
                    t && v > t
                      ? { from: v, to: v }
                      : {
                          from: v,
                          to:
                            tempCustomCompareDates?.to ??
                            compareDateRange?.to ??
                            '',
                        },
                  );
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: lookbackMinDate || undefined,
                }}
                sx={{ width: 140, ...controlSx }}
              />
              <TextField
                type="date"
                label="To"
                size="small"
                value={tempCustomCompareDates?.to ?? compareDateRange?.to ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  const f =
                    tempCustomCompareDates?.from ?? compareDateRange?.from;
                  setTempCustomCompareDates(
                    f && v < f
                      ? { from: f, to: f }
                      : {
                          from:
                            tempCustomCompareDates?.from ??
                            compareDateRange?.from ??
                            '',
                          to: v,
                        },
                  );
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min:
                    (tempCustomCompareDates?.from ?? compareDateRange?.from) ||
                    undefined,
                  max: new Date().toISOString().split('T')[0],
                }}
                sx={{ width: 140, ...controlSx }}
              />
              <Button
                variant="contained"
                size="small"
                className={KIPIFY_FILTER_APPLY_BUTTON_CLASS}
                onClick={() => {
                  if (
                    tempCustomCompareDates?.from &&
                    tempCustomCompareDates?.to &&
                    tempCustomCompareDates.from <= tempCustomCompareDates.to
                  ) {
                    if (!isRangeWithinLookback(tempCustomCompareDates)) {
                      snackbar(
                        getLookbackCustomRangeBlockedMessage(
                          lookbackDays,
                          'compare',
                        ),
                        'warning',
                      );
                      return;
                    }
                    setCompareDateRange(tempCustomCompareDates);
                    setShowCustomCompareDates(false);
                    setTempCustomCompareDates(null);
                  }
                }}
                disabled={
                  !tempCustomCompareDates?.from ||
                  !tempCustomCompareDates?.to ||
                  tempCustomCompareDates.from > tempCustomCompareDates.to
                }
                sx={{
                  minHeight: controlHeight,
                  borderRadius: controlRadius,
                  bgcolor: palette.primary.main,
                  color: palette.primary.contrastText,
                  minWidth: 60,
                }}
              >
                Apply
              </Button>
            </>
          )}
          {lookbackDays !== null && lookbackDays !== undefined && (
            <Typography
              variant="caption"
              sx={{
                width: '100%',
                color: 'warning.main',
                fontWeight: 500,
                mt: 0.5,
              }}
            >
              {getLookbackPlanLimitCaption(lookbackDays)}
            </Typography>
          )}

          <Box sx={{ width: { xs: '100%', sm: 280 }, flexShrink: 0 }}>
            <Typography component="label" sx={labelSx}>
              Search
            </Typography>
            <TextField
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{
                width: '100%',
                ...controlSx,
                '& .MuiOutlinedInput-root': {
                  alignItems: 'center',
                  height: controlHeight,
                  maxHeight: controlHeight,
                  boxSizing: 'border-box',
                },
                '& .MuiInputBase-input': {
                  py: 0.5,
                  lineHeight: 1.43,
                  boxSizing: 'border-box',
                },
              }}
              InputProps={{
                startAdornment: (
                  <Box
                    sx={{
                      mr: 1,
                      color: 'text.secondary',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    🔍
                  </Box>
                ),
                endAdornment: searchTerm && (
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                    sx={{ mr: -1 }}
                  >
                    ✕
                  </IconButton>
                ),
              }}
            />
          </Box>
          <Box sx={{ flexShrink: 0 }}>
            <Typography component="label" sx={labelSx}>
              Filters
            </Typography>
            <Button
              size="small"
              variant={showMoreFilters ? 'contained' : 'outlined'}
              startIcon={<FilterListIcon sx={{ fontSize: 16 }} />}
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    fontSize: 16,
                    transform: showMoreFilters ? 'rotate(180deg)' : 'none',
                  }}
                />
              }
              onClick={() => setShowMoreFilters((v) => !v)}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                height: controlHeight,
                minHeight: controlHeight,
                boxSizing: 'border-box',
                borderRadius: controlRadius,
                px: 1.5,
                py: 0,
                whiteSpace: 'nowrap',
                fontSize: '0.8125rem',
                borderColor:
                  activeFilterCount > 0 ? palette.primary.main : '#e0e0e0',
                color: showMoreFilters ? '#fff' : 'text.secondary',
                bgcolor: showMoreFilters ? palette.primary.main : 'white',
                '&:hover': {
                  bgcolor: showMoreFilters
                    ? palette.primary.light
                    : 'rgba(0,0,0,0.04)',
                  borderColor: palette.primary.main,
                },
              }}
            >
              Filters
              {activeFilterCount > 0 && (
                <Box
                  component="span"
                  sx={{
                    ml: 0.75,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: showMoreFilters
                      ? 'rgba(255,255,255,0.3)'
                      : palette.primary.main,
                    color: '#fff',
                  }}
                >
                  {activeFilterCount}
                </Box>
              )}
            </Button>
          </Box>
        </Box>

        {activeFilterCount > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              mb: 1.5,
              flexWrap: 'wrap',
            }}
          >
            {countryFilter.map((c) => (
              <Chip
                key={c}
                size="small"
                label={c}
                onDelete={() =>
                  setCountryFilter((p) => p.filter((x) => x !== c))
                }
                sx={{ borderRadius: 1, fontSize: 12 }}
              />
            ))}
            {customerTypeFilter && (
              <Chip
                size="small"
                label={
                  customerTypeFilter === 'First time' ? 'New' : 'Recurring'
                }
                onDelete={() => setCustomerTypeFilter('')}
                sx={{ borderRadius: 1, fontSize: 12 }}
              />
            )}
            {tagFilter.map((t) => (
              <Chip
                key={t}
                size="small"
                label={t}
                onDelete={() => setTagFilter((p) => p.filter((x) => x !== t))}
                sx={{ borderRadius: 1, fontSize: 12 }}
              />
            ))}
            {collectionFilter.map((c) => (
              <Chip
                key={c}
                size="small"
                label={c}
                onDelete={() =>
                  setCollectionFilter((p) => p.filter((x) => x !== c))
                }
                sx={{ borderRadius: 1, fontSize: 12 }}
              />
            ))}
            {vendorFilter.map((v) => (
              <Chip
                key={v}
                size="small"
                label={v}
                onDelete={() =>
                  setVendorFilter((p) => p.filter((x) => x !== v))
                }
                sx={{ borderRadius: 1, fontSize: 12 }}
              />
            ))}
            {productTypeFilter.map((p) => (
              <Chip
                key={p}
                size="small"
                label={p}
                onDelete={() =>
                  setProductTypeFilter((prev) => prev.filter((x) => x !== p))
                }
                sx={{ borderRadius: 1, fontSize: 12 }}
              />
            ))}
            <Button
              size="small"
              onClick={() => {
                setCountryFilter([]);
                setCustomerTypeFilter('');
                setTagFilter([]);
                setCollectionFilter([]);
                setVendorFilter([]);
                setProductTypeFilter([]);
              }}
              sx={{
                textTransform: 'none',
                fontSize: 12,
                color: 'text.secondary',
              }}
            >
              Clear all
            </Button>
          </Box>
        )}

        <Collapse in={showMoreFilters} timeout={200}>
          <Box
            sx={{
              mt: 1,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
              gap: 1.5,
              mb: 1.5,
              p: 2,
              borderRadius: 2,
              backgroundColor: palette.background.tableRow,
              border: '1px solid',
              borderColor: palette.grey[300],
            }}
          >
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
              >
                Country
              </Typography>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={FILTER_COUNTRY_OPTIONS}
                value={countryFilter}
                onChange={(_, v) => setCountryFilter(v)}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      checked={selected}
                      sx={{ mr: 0.5, p: 0 }}
                      size="small"
                    />
                    <Typography variant="body2">{option}</Typography>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select countries..."
                    sx={{
                      '& .MuiOutlinedInput-root': { backgroundColor: 'white' },
                    }}
                  />
                )}
                renderTags={() => null}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
              >
                Customer type
              </Typography>
              <Select
                size="small"
                fullWidth
                displayEmpty
                value={customerTypeFilter}
                onChange={(e) => setCustomerTypeFilter(e.target.value)}
                sx={{ backgroundColor: 'white' }}
                renderValue={(v) =>
                  v
                    ? v === 'First time'
                      ? 'New'
                      : 'Recurring'
                    : 'All customers'
                }
              >
                <MenuItem value="">All customers</MenuItem>
                <MenuItem value="First time">New (first time)</MenuItem>
                <MenuItem value="recurring">Recurring</MenuItem>
              </Select>
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
              >
                Tag
              </Typography>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={availableTags}
                value={tagFilter}
                onChange={(_, v) => setTagFilter(v)}
                renderOption={(props, o, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      checked={selected}
                      sx={{ mr: 0.5, p: 0 }}
                      size="small"
                    />
                    <Typography variant="body2">{o}</Typography>
                  </li>
                )}
                renderInput={(p) => (
                  <TextField
                    {...p}
                    placeholder="Select tags..."
                    sx={filterInputSx}
                  />
                )}
                renderTags={() => null}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
              >
                Collection
              </Typography>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={availableCollections}
                value={collectionFilter}
                onChange={(_, v) => setCollectionFilter(v)}
                renderOption={(props, o, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      checked={selected}
                      sx={{ mr: 0.5, p: 0 }}
                      size="small"
                    />
                    <Typography variant="body2">{o}</Typography>
                  </li>
                )}
                renderInput={(p) => (
                  <TextField
                    {...p}
                    placeholder="Select collections..."
                    sx={filterInputSx}
                  />
                )}
                renderTags={() => null}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
              >
                Vendor
              </Typography>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={availableVendors}
                value={vendorFilter}
                onChange={(_, v) => setVendorFilter(v)}
                renderOption={(props, o, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      checked={selected}
                      sx={{ mr: 0.5, p: 0 }}
                      size="small"
                    />
                    <Typography variant="body2">{o}</Typography>
                  </li>
                )}
                renderInput={(p) => (
                  <TextField
                    {...p}
                    placeholder="Select vendors..."
                    sx={filterInputSx}
                  />
                )}
                renderTags={() => null}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
              >
                Product Type
              </Typography>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={availableProductTypes}
                value={productTypeFilter}
                onChange={(_, v) => setProductTypeFilter(v)}
                renderOption={(props, o, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      checked={selected}
                      sx={{ mr: 0.5, p: 0 }}
                      size="small"
                    />
                    <Typography variant="body2">{o}</Typography>
                  </li>
                )}
                renderInput={(p) => (
                  <TextField
                    {...p}
                    placeholder="Select types..."
                    sx={filterInputSx}
                  />
                )}
                renderTags={() => null}
              />
            </Box>
          </Box>
        </Collapse>

        {searchTerm && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {filteredData.length} of {structuralFilteredData.length} products
              match &quot;{searchTerm}&quot;
              {' · '}
              <Box component="span" sx={{ opacity: 0.85 }}>
                % of revenue / % of products / cumulative % are vs the full list
                (current filters), not only these rows.
              </Box>
            </Typography>
          </Box>
        )}

        {loading ? (
          <Card sx={{ mt: 1, mb: 2, borderRadius: 2, overflow: 'hidden' }}>
            <CardContent sx={{ pt: 3, pb: 3, px: 2.5 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, textAlign: 'center', fontWeight: 500 }}
              >
                Preparing your products analysis…
              </Typography>
              <Box
                sx={{
                  height: 200,
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 0.5,
                  justifyContent: 'center',
                }}
              >
                {Array.from({ length: 48 }).map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 4,
                      minWidth: 4,
                      height: `${28 + Math.sin(i * 0.5) * 30 + (i % 7) * 8}%`,
                      maxHeight: '95%',
                      borderRadius: '2px 2px 0 0',
                      bgcolor: palette.primary.main,
                      animation: `${barPulseKeyframes} 1.8s ease-in-out infinite`,
                      animationDelay: `${(i % 12) * 0.08}s`,
                      transformOrigin: 'bottom',
                    }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        ) : fullParetoData.length === 0 ? (
          <Card sx={{ mt: 1 }}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No data for products analysis
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try widening the date range or removing some filters.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Chart: card con contrasto e tooltip leggibile */}
            <Card
              sx={{
                mt: 1,
                mb: 2,
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow:
                  '0 4px 24px -4px rgba(0,0,0,0.08), 0 2px 8px -2px rgba(0,0,0,0.04)',
                transition: 'box-shadow 0.2s ease',
                '&:hover': {
                  boxShadow:
                    '0 8px 32px -4px rgba(0,0,0,0.12), 0 4px 12px -2px rgba(0,0,0,0.06)',
                },
              }}
            >
              <CardContent sx={{ pt: 2, pb: 2, px: 2.5 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1, fontWeight: 500 }}
                >
                  Real sales data · {periodLabel} · {chartBars.length} products
                  (bar = % of revenue)
                </Typography>
                <Box
                  sx={{
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: palette.grey[300],
                    overflow: 'hidden',
                  }}
                >
                  {/* Area grafico: altezza fissa + padding-bottom così la scrollbar sta sotto le barre */}
                  <Box
                    sx={{
                      height: 200,
                      pb: 3,
                      boxSizing: 'content-box',
                      display: 'flex',
                      alignItems: 'flex-end',
                      gap: 0.5,
                      overflowX: 'auto',
                      overflowY: 'hidden',
                      px: 0.5,
                      pt: 0.5,
                      background: `linear-gradient(to top, ${palette.primary.light}40, transparent)`,
                      '&::-webkit-scrollbar': { height: 10 },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: palette.grey[200],
                        borderTop: '1px solid',
                        borderColor: palette.grey[300],
                        borderRadius: '0 0 4px 4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: palette.grey[500],
                        borderRadius: 5,
                        border: '2px solid',
                        borderColor: palette.grey[200],
                        '&:hover': { backgroundColor: palette.grey[600] },
                      },
                    }}
                  >
                    {chartBars.map((row) => {
                      const isTop80 =
                        row.cumulativeShare <= CUMULATIVE_THRESHOLD_PCT;
                      return (
                        <Tooltip
                          key={row.productId}
                          title={
                            <Box
                              component="span"
                              sx={{
                                display: 'block',
                                p: 0,
                                textAlign: 'center',
                              }}
                            >
                              {row.imageUrl && (
                                <Box
                                  sx={{
                                    width: 96,
                                    height: 96,
                                    flexShrink: 0,
                                    borderRadius: 1,
                                    mb: 1,
                                    mx: 'auto',
                                    overflow: 'hidden',
                                    bgcolor: 'rgba(255,255,255,0.08)',
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={row.imageUrl}
                                    alt=""
                                    sx={{
                                      display: 'block',
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'contain',
                                    }}
                                  />
                                </Box>
                              )}
                              <Typography
                                variant="caption"
                                component="div"
                                sx={{
                                  fontWeight: 700,
                                  mb: 0.5,
                                  color: '#fff',
                                  fontSize: '0.75rem',
                                }}
                              >
                                {row.productTitle}
                              </Typography>
                              <Typography
                                variant="caption"
                                component="div"
                                sx={{ color: 'rgba(255,255,255,0.9)' }}
                              >
                                Sold: {row.totalSold}
                              </Typography>
                              <Typography
                                variant="caption"
                                component="div"
                                sx={{ color: 'rgba(255,255,255,0.9)' }}
                              >
                                Revenue: {fmtEur(row.totalRevenue)}
                              </Typography>
                              <Typography
                                variant="caption"
                                component="div"
                                sx={{ color: 'rgba(255,255,255,0.9)' }}
                              >
                                % of revenue: {row.revenueShare.toFixed(2)}%
                              </Typography>
                              <Typography
                                variant="caption"
                                component="div"
                                sx={{ color: 'rgba(255,255,255,0.9)' }}
                              >
                                Top {row.productSharePct.toFixed(0)}% of
                                products (by revenue)
                              </Typography>
                              {isTop80 && (
                                <Typography
                                  variant="caption"
                                  component="div"
                                  sx={{
                                    color: palette.primary.light,
                                    fontWeight: 600,
                                    mt: 0.5,
                                  }}
                                >
                                  Top 80% revenue
                                </Typography>
                              )}
                            </Box>
                          }
                          placement="top"
                          arrow
                          componentsProps={{
                            tooltip: {
                              sx: {
                                background:
                                  'linear-gradient(to bottom, rgba(0, 45, 28, 1), rgba(3, 12, 22, 1))',
                                color: '#fff',
                                px: 1.5,
                                py: 1.25,
                                fontSize: '0.8125rem',
                                fontWeight: 500,
                                boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                                maxWidth: 320,
                                border: '1px solid rgba(255,255,255,0.08)',
                                borderRadius: 1,
                              },
                            },
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              display: 'block',
                              width: 3,
                              minWidth: 3,
                              borderRadius: '2px 2px 0 0',
                              background: isTop80
                                ? `linear-gradient(to top, ${palette.primary.main}, ${palette.primary.light})`
                                : 'linear-gradient(to top, rgba(0,0,0,0.2), rgba(0,0,0,0.08))',
                              height: `${Math.min(Math.max(row.heightPct, 2), 100)}%`,
                              cursor: 'pointer',
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mt: 1.5,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: 0.5,
                        background: `linear-gradient(to top, ${palette.primary.main}, ${palette.primary.light})`,
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Top 80% (revenue)
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: 0.5,
                        bgcolor: 'rgba(0,0,0,0.15)',
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Long tail
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Tabella */}
            <Card
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow:
                  '0 4px 24px -4px rgba(0,0,0,0.08), 0 2px 8px -2px rgba(0,0,0,0.04)',
                transition: 'box-shadow 0.2s ease',
                '&:hover': {
                  boxShadow:
                    '0 8px 32px -4px rgba(0,0,0,0.12), 0 4px 12px -2px rgba(0,0,0,0.06)',
                },
              }}
            >
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table
                  sx={{
                    minWidth: { xs: 800, sm: 'auto' },
                    '& .MuiTableCell-root': { px: 1.5 },
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ bgcolor: palette.background.tableRow }}>
                      <TableCell>#</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Sold</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell align="right">% of revenue</TableCell>
                      <TableCell align="right">% of products</TableCell>
                      <TableCell align="right">Cumulative %</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayParetoData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            No products match your search. Clear the search box
                            to see the full ranking.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      displayParetoData
                        .slice(page * rowsPerPage, (page + 1) * rowsPerPage)
                        .map((row) => (
                          <TableRow key={row.productId} hover>
                            <TableCell>{row.paretoIndex}</TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.5,
                                  flexWrap: 'wrap',
                                }}
                              >
                                {row.imageUrl ? (
                                  <Box
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      borderRadius: 1,
                                      overflow: 'hidden',
                                      flexShrink: 0,
                                      bgcolor: palette.grey[200],
                                    }}
                                  >
                                    <Box
                                      component="img"
                                      src={row.imageUrl}
                                      alt=""
                                      sx={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                      }}
                                    />
                                  </Box>
                                ) : (
                                  <Box
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      borderRadius: 1,
                                      bgcolor: palette.grey[200],
                                      flexShrink: 0,
                                    }}
                                  />
                                )}
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {row.productTitle}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">{row.totalSold}</TableCell>
                            <TableCell align="right">
                              {fmtEur(row.totalRevenue)}
                            </TableCell>
                            <TableCell align="right">
                              {row.revenueShare.toFixed(2)}%
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: 'text.secondary' }}
                            >
                              {row.productSharePct.toFixed(1)}%
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 600,
                                color: palette.primary.main,
                              }}
                            >
                              {row.cumulativeShare.toFixed(2)}%
                            </TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1,
                  px: 2,
                  borderTop: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {displayParetoData.length === 0
                    ? '0'
                    : `${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, displayParetoData.length)}`}{' '}
                  of {displayParetoData.length}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    sx={{ textTransform: 'none' }}
                  >
                    Previous
                  </Button>
                  <Button
                    size="small"
                    disabled={
                      displayParetoData.length === 0 ||
                      page >=
                        Math.ceil(displayParetoData.length / rowsPerPage) - 1
                    }
                    onClick={() => setPage((p) => p + 1)}
                    sx={{ textTransform: 'none' }}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            </Card>
          </>
        )}
      </ContentContainer>
    </PageContainer>
  );
};

export default Pareto;
